// Theme store — colour mode and compact density preferences.
// `mode` and `compact` are persisted to localStorage; `darkMode` is the resolved value.
// Export `useAntdTheme()` for use in AntD ConfigProvider.
//
// Task 355.1 (Console Glass, Phase 1): the default mode follows the OS (`prefers-color-scheme`).
// An explicit Dark Mode toggle pins light or dark; "Match system theme" returns to following the OS.

import { useMemo } from 'react';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { theme as antdTheme, type ThemeConfig } from 'antd';
import { DAY_FROST, NIGHT_GLASS } from '../shared/theme/glassTheme';

export type ThemeMode = 'system' | 'light' | 'dark';

const DARK_QUERY = '(prefers-color-scheme: dark)';

interface ThemeState {
  mode: ThemeMode;
  /** OS preference, tracked live; only used while `mode === 'system'`. */
  systemDark: boolean;
  /** Resolved: true when the dark palette is in effect. */
  darkMode: boolean;
  compact: boolean;
  /** Pins the opposite of what is currently shown. */
  toggleDarkMode: () => void;
  setMode: (mode: ThemeMode) => void;
  setSystemDark: (dark: boolean) => void;
  toggleCompact: () => void;
}

interface PersistedTheme {
  mode: ThemeMode;
  compact: boolean;
}

export function resolveDarkMode(mode: ThemeMode, systemDark: boolean): boolean {
  return mode === 'dark' || (mode === 'system' && systemDark);
}

/**
 * v0 persisted `{ darkMode, compact }`. `darkMode: false` was the default rather than a choice, so it
 * becomes "follow the OS"; `darkMode: true` was an explicit choice and stays pinned to dark.
 */
export function migrateThemeState(persisted: unknown, version: number): PersistedTheme {
  const state = (persisted ?? {}) as Partial<PersistedTheme> & { darkMode?: boolean };
  if (version < 1) {
    return { mode: state.darkMode ? 'dark' : 'system', compact: state.compact ?? false };
  }
  return { mode: state.mode ?? 'system', compact: state.compact ?? false };
}

function readSystemDark(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia(DARK_QUERY).matches;
}

export const useThemeStore = create<ThemeState>()(
  persist(
    (set, get) => ({
      mode: 'system',
      systemDark: readSystemDark(),
      darkMode: resolveDarkMode('system', readSystemDark()),
      compact: false,

      toggleDarkMode: () => {
        const mode: ThemeMode = get().darkMode ? 'light' : 'dark';
        set({ mode, darkMode: resolveDarkMode(mode, get().systemDark) });
      },
      setMode: (mode) => set({ mode, darkMode: resolveDarkMode(mode, get().systemDark) }),
      setSystemDark: (systemDark) =>
        set({ systemDark, darkMode: resolveDarkMode(get().mode, systemDark) }),
      toggleCompact: () => set({ compact: !get().compact }),
    }),
    {
      name: 'bancorix:theme',
      version: 1,
      migrate: migrateThemeState,
      partialize: (state): PersistedTheme => ({ mode: state.mode, compact: state.compact }),
      merge: (persisted, current) => {
        const saved = migrateThemeState(persisted, 1);
        return { ...current, ...saved, darkMode: resolveDarkMode(saved.mode, current.systemDark) };
      },
    },
  ),
);

// Follow OS changes live (e.g. macOS auto appearance at sunset).
if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
  window
    .matchMedia(DARK_QUERY)
    .addEventListener?.('change', (e) => useThemeStore.getState().setSystemDark(e.matches));
}

// Derives the AntD ThemeConfig from current store state.
// Use in <ConfigProvider theme={useAntdTheme()}>.
export function useAntdTheme(): ThemeConfig {
  const { darkMode, compact } = useThemeStore();
  return useMemo(() => {
    const glass = darkMode ? NIGHT_GLASS : DAY_FROST;
    return {
      algorithm: [
        ...(darkMode ? [antdTheme.darkAlgorithm] : [antdTheme.defaultAlgorithm]),
        ...(compact ? [antdTheme.compactAlgorithm] : []),
      ],
      token: glass.token,
      components: glass.components,
    };
  }, [darkMode, compact]);
}
